// The kanban card: a label with everything needed to reorder printed on it, so nobody has
// to look anything up. It sits in the bin at the reorder level. Reaching it means: scan it
// (that raises the reorder), then put it in the reorder slot on the shelf.
//
// Layout: 4 × 6 in, landscape, one card to a page. Picture and QR code on the left; on the
// right the description, part number, vendor and link, the order quantity (large), lead
// time, bin location and "Card 1 of 2"; the instruction along the foot.

import { useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams, type NavigateFunction } from 'react-router-dom';
import qrcode from 'qrcode-generator';
import { leadTime } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { fetchRows, useRows, type Row } from '../../lib/api';
import { qty } from '../../lib/format';
import { addKanbanCard, useVendors } from '../../lib/inventory';
import { Btn, Empty, Loading } from '../../ui/kit';
import { useSignedUrl } from '../job/Files';
import { cap, useInventoryNames } from './names';

/** The code as a QR picture. Drawn as plain squares so it prints sharp at any size. */
export function QR({ text, size = 120 }: { text: string; size?: number }) {
  const cells = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    const dark: string[] = [];
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) dark.push(`M${c} ${r}h1v1h-1z`);
    return { n, path: dark.join('') };
  }, [text]);
  // a quiet border of four squares, as the readers expect
  return (
    <svg width={size} height={size} viewBox={`-4 -4 ${cells.n + 8} ${cells.n + 8}`} role="img" aria-label={`QR code ${text}`} shapeRendering="crispEdges" style={{ background: '#fff' }}>
      <path d={cells.path} fill="#000" />
    </svg>
  );
}

/** One click from the item: make sure the material has a card, then open the print page with the print dialog. */
export async function printKanban(materialId: string, navigate: NavigateFunction): Promise<void> {
  const cards = await fetchRows('label', { eq: { kind: 'kanban', target_id: materialId } });
  if (cards.length === 0) await addKanbanCard(materialId);
  navigate(`/inventory/cards/${materialId}?print=1`);
}

export function KanbanCards() {
  const { materialId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const names = useInventoryNames();
  const vendors = useVendors();
  const material = useRows('material', { eq: { id: materialId } });
  const cards = useRows('label', { eq: { kind: 'kanban', target_id: materialId }, order: ['card_no'] });
  const m = material.rows[0];
  const picture = useRows('file', { eq: { id: m?.picture_file_id }, enabled: !!m?.picture_file_id }).rows[0];
  const pictureUrl = useSignedUrl(picture?.storage_path);
  const printed = useRef(false);
  useCrumb([{ label: names.rail, to: '/inventory?tab=stock' }, { label: m ? `${cap(names.kanban_card)} · ${m.description}` : cap(names.kanban_card) }]);

  // the page itself is the card: 6 × 4 in with no margin, only while this screen is open
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = '@page { size: 6in 4in; margin: 0; }';
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const ready = !material.loading && !cards.loading && !vendors.loading && cards.rows.length > 0 && (!m?.picture_file_id || !!pictureUrl);
  // arriving from "Print kanban card": open the print dialog once everything on the card has loaded
  useEffect(() => {
    if (!ready || printed.current || params.get('print') !== '1') return;
    printed.current = true;
    const t = window.setTimeout(() => window.print(), 600);
    return () => window.clearTimeout(t);
  }, [ready, params]);

  if (material.loading || cards.loading) return <Loading />;
  if (!m) return <Empty>This material is no longer there.</Empty>;
  const vendor = vendors.byId.get(m.vendor_id) ?? null;
  const lead = leadTime(m as { vendor_id: string | null; lead_time_days: number | null }, vendor as { id: string; lead_time_days: number | null } | null);
  const link = String(m.vendor_url ?? vendor?.url ?? '').replace(/^https?:\/\//, '');

  return (
    <div>
      <div className="row mid noprint" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <span>
          <Link to="/inventory?tab=stock">← {names.stock}</Link> <small>· {cards.rows.length} {cards.rows.length === 1 ? 'card' : 'cards'}, one per bin. Printed 4 × 6 in, landscape.</small>
        </span>
        <span className="row mid" style={{ gap: 8 }}>
          <Btn small onClick={() => void addKanbanCard(m.id)}>
            Add a card (another bin)
          </Btn>
          <Btn primary read onClick={() => window.print()}>
            Print
          </Btn>
        </span>
      </div>
      {cards.rows.length === 0 && (
        <Empty>
          No card yet.{' '}
          <button type="button" className="linklike read" onClick={() => void printKanban(m.id, navigate)}>
            Make one
          </button>
        </Empty>
      )}
      {cards.rows.map((c: Row) => (
        <div key={c.id} className="kanban-card">
          <div className="kc-left">
            <div className="kc-pic">{pictureUrl ? <img src={pictureUrl} alt="" /> : <span>no picture</span>}</div>
            <QR text={c.code} size={132} />
            <div className="kc-code">{c.code}</div>
          </div>
          <div className="kc-right">
            <div className="kc-name">{m.description}</div>
            <div className="kc-row">
              <span>Part no.</span>
              <b>{m.part_number ?? '—'}</b>
            </div>
            <div className="kc-row">
              <span>Vendor</span>
              <b>{vendor?.name ?? '—'}</b>
            </div>
            {link && <div className="kc-link">{link.length > 64 ? `${link.slice(0, 63)}…` : link}</div>}
            <div className="kc-order">
              <span>{cap(names.order_quantity)}</span>
              <b>
                {m.order_quantity === null ? '—' : qty(m.order_quantity)} <small>{m.unit ?? ''}</small>
              </b>
            </div>
            <div className="kc-row">
              <span>Lead time</span>
              <b>{lead === null ? '—' : `${lead} days`}</b>
            </div>
            <div className="kc-row">
              <span>Bin</span>
              <b>{m.bin_location ?? '—'}</b>
            </div>
            <div className="kc-row">
              <span>Card</span>
              <b>
                {c.card_no} of {cards.rows.length}
              </b>
            </div>
          </div>
          <div className="kc-foot">Reached this card? Scan it, then put it in the reorder slot.</div>
        </div>
      ))}
    </div>
  );
}
